/*
 * Copyright (c) 2022 automata-network/tgb-donation authors and contributors.
 * This software may be modified and distributed under the terms of MIT license.
 * Refer to the LICENSE file for details.
 */
import { ReactNode, useCallback, useRef, useState } from 'react'

import { nanoid } from 'nanoid'
import styled, { css } from 'styled-components'

import FlexBox from 'src/components/Common/FlexBox'
import { ERROR_MESSAGE } from 'src/constants/errors'
import { pxToVw } from 'src/functions/units'
import { CouldBe } from 'src/types/main'

type Props = {
  className?: string
  label: string
  minLength?: number
  maxLength?: number
  required?: boolean
  rule?: RegExp
  ruleErrorMessage?: string
  type?: 'text' | 'email'
  value?: string
  help?: ReactNode
  onChange?: (value: string) => void
  onError?: (message: string) => void
}

export const IdentityInputField = ({
  className,
  label,
  minLength = 0,
  maxLength = 225,
  required = false,
  rule,
  ruleErrorMessage,
  type = 'text',
  value = '',
  help,
  onChange,
  onError
}: Props) => {
  const inputTextIdRef = useRef<string>(`input_${nanoid(8)}`)

  const [isError, setIsError] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<CouldBe<string>>()

  const handleInputChange = useCallback(
    (value: string) => {
      if (
        (rule && !rule.test(value)) ||
        (required && !value.length) ||
        value.length < minLength ||
        value.length > maxLength
      ) {
        let _errorMessage: string = ''
        if (required && !value.length) {
          _errorMessage = ERROR_MESSAGE.IDENTITY_IS_REQUIRED.replace('{{label_name}}', label)
        } else if (rule && !rule.test(value)) {
          _errorMessage = ruleErrorMessage || `Invalid ${label.toLowerCase()} format`
        } else if (value.length < minLength || value.length > maxLength) {
          _errorMessage = ERROR_MESSAGE.IDENTITY_MIN_MAX_EXCEEDED.replace(
            /{{(?:label_name|min_length|max_length)}}/g,
            (matched, ...args) => {
              return matched === '{{label_name}}'
                ? label
                : matched === '{{min_length}}'
                ? minLength.toString()
                : matched === '{{max_length}}'
                ? maxLength.toString()
                : ''
            }
          )
        }

        setIsError(true)
        setErrorMessage(_errorMessage)
        onError && onError(_errorMessage)
      } else {
        setIsError(false)
        setErrorMessage(undefined)
        onError && onError('')
      }

      onChange && onChange(value)
    },
    [label, maxLength, minLength, onChange, onError, required, rule, ruleErrorMessage]
  )

  return (
    <FlexBox className={className} flexDirection="column" rowGap={4}>
      <StyledLabel htmlFor={inputTextIdRef.current} $error={isError}>
        {label}
      </StyledLabel>
      <StyledInput
        id={inputTextIdRef.current}
        type={type}
        value={value}
        $error={isError}
        onChange={(e) => handleInputChange(e.currentTarget.value)}
      />
      <StyledFieldFooter justifyContent="space-between" alignItems="flex-start" columnGap={24}>
        {isError ? <StyledError>{errorMessage}</StyledError> : help || 'Required'}
        <StyledCharCounter>
          {value.length}/{maxLength}
        </StyledCharCounter>
      </StyledFieldFooter>
    </FlexBox>
  )
}

const StyledLabel = styled.label<{
  $error: boolean
}>`
  ${({ theme, $error }) => css`
    font-size: ${pxToVw(theme.units.fontSize.body2)};
    color: ${!$error ? theme.colors.gray700 : theme.colors.red};

    ${theme.mediaQuery.mobile`
      font-size: ${pxToVw(theme.units.fontSize.body2, 720)};
    `}
  `}
`

const StyledInput = styled.input<{
  $error: boolean
}>`
  width: 100%;
  height: ${pxToVw(44)};
  appearance: none;
  padding-inline: ${pxToVw(13)};

  ${({ theme, $error }) => css`
    background: ${theme.colors.bg};
    border: 1px solid ${!$error ? theme.colors.gray300 : theme.colors.red};
    border-radius: ${pxToVw(theme.units.borderRadius.small)};
    font-size: ${pxToVw(theme.units.fontSize.body3)};
    color: ${!$error ? undefined : theme.colors.red};
  `}

  ${({ theme }) => css`
    ${theme.mediaQuery.mobile`
      height: ${pxToVw(44, 720)};
      padding-inline: ${pxToVw(13, 720)};
      border-radius: ${pxToVw(theme.units.borderRadius.small, 720)};
      font-size: ${pxToVw(theme.units.fontSize.body3, 720)};
    `}
  `}
`

const StyledFieldFooter = styled(FlexBox)`
  width: 100%;
  font-size: ${pxToVw(18)};
  color: ${({ theme }) => theme.colors.gray};

  ${({ theme }) => css`
    ${theme.mediaQuery.mobile`
      font-size: ${pxToVw(18, 720)};
    `}
  `}
`

const StyledError = styled.div`
  font-weight: 500;
  color: ${({ theme }) => theme.colors.red};
`

const StyledCharCounter = styled.div`
  margin-left: auto;
  font-size: ${({ theme }) => pxToVw(theme.units.fontSize.regular)};
  font-weight: 500;
  color: ${({ theme }) => theme.colors.gray};

  ${({ theme }) => css`
    ${theme.mediaQuery.mobile`
      font-size: ${pxToVw(theme.units.fontSize.regular, 720)};
    `}
  `}
`
